import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, useTheme, Paper, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useMatrixData } from '../../usePlotData';
import { BarChartComponent } from './BarChart';
import { PieChartComponent } from './PieChart';
import { RadarChartComponent } from './RadarChart';
import { CustomTooltip } from './CustomTooltip';
import { CHART_TYPES, CHART_DIMENSIONS, TEXT_STYLES, PAPER_STYLES, TITLE_STYLES } from './styles';
import { useTranslation } from 'react-i18next';

const FONT_FAMILY = 'Roboto, "Helvetica Neue", Arial, sans-serif';

export const Matrix = ({ klausimas, atsakymai }) => {
    const { data, legend } = useMatrixData(klausimas, atsakymai);
    const [chartType, setChartType] = useState(CHART_TYPES.BAR);
    // Always show all legends
    const [selectedLegends, setSelectedLegends] = useState(() => legend ? Array.from({ length: legend.length }, (_, i) => i) : []);
    const [sortByLegendIndex, setSortByLegendIndex] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc');
    const theme = useTheme();
    const chartRef = useRef(null);
    const [yAxisWidth, setYAxisWidth] = useState(300);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const { i18n } = useTranslation();
    const lng = i18n.language === 'en' ? 'default' : i18n.language;

    // Effect for handling chart type transitions
    useEffect(() => {
        setIsTransitioning(true);
        const timer = setTimeout(() => {
            setIsTransitioning(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [chartType]);

    useEffect(() => {
        const calculateYAxisWidth = () => {
            try {
                const screenWidth = window.innerWidth;
                const calculatedWidth = Math.min(Math.max(screenWidth * 0.25, 200), 150);
                setYAxisWidth(calculatedWidth);
            } catch (error) {
                console.error('Error calculating axis width:', error);
                setYAxisWidth(150); // Fallback width
            }
        };

        calculateYAxisWidth();
        const resizeHandler = debounce(calculateYAxisWidth, 150);
        window.addEventListener('resize', resizeHandler);
        
        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, []);

    // Debounce function
    const debounce = (func, wait) => {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    };

    useEffect(() => {
        // Only initialize sort index when legend data is available
        if (legend && legend.length > 0) {
            setSortByLegendIndex(0); // Set initial sort index
        }
    }, [legend]);

    if (!data || !legend) {
        return (
            <Box sx={{ 
                p: 2, 
                textAlign: 'center', 
                color: 'text.secondary',
                minHeight: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center' 
            }}>
                Nėra duomenų atvaizdavimui
            </Box>
        );
    }

    const chartTitle = typeof klausimas?.title === 'object' ? klausimas?.title?.[lng] || '' : klausimas?.title || '';

    const truncateLabel = (label) => {
        if (!label) return '';
        
        const words = label.split(/\s+/);
        if (words.length <= 1) {
            if (label.length > 40) {
                const chunks = label.match(/.{1,40}/g) || [];
                return chunks;
            }
            return label;
        }

        const MAX_LINE_LENGTH = 40;
        const MAX_WORD_LENGTH = 40;
        
        let lines = [];
        let currentLine = [];
        let currentLength = 0;

        for (let word of words) {
            if (word.length > MAX_WORD_LENGTH) {
                if (currentLine.length > 0) {
                    lines.push(currentLine.join(' '));
                }
                const chunks = word.match(new RegExp(`.{1,${MAX_WORD_LENGTH}}`, 'g')) || [];
                lines.push(...chunks);
                currentLine = [];
                currentLength = 0;
                continue;
            }

            const wordLength = word.length;
            
            if (currentLine.length === 0) {
                currentLine.push(word);
                currentLength = wordLength;
            } else if (currentLength + 1 + wordLength <= MAX_LINE_LENGTH) {
                currentLine.push(word);
                currentLength += 1 + wordLength;
            } else {
                lines.push(currentLine.join(' '));
                currentLine = [word];
                currentLength = wordLength;
            }
        }

        if (currentLine.length > 0) {
            lines.push(currentLine.join(' '));
        }

        return lines;
    };

    const calculateDimensions = (data) => {
        let totalHeight = 0;
        let maxLines = 1;
        
        data.forEach(item => {
            const lines = truncateLabel(item.text);
            const lineCount = Array.isArray(lines) ? lines.length : 1;
            maxLines = Math.max(maxLines, lineCount);
            totalHeight += CHART_DIMENSIONS.ITEM.BASE_HEIGHT + 
                          (lineCount > 1 ? (lineCount - 1) * CHART_DIMENSIONS.ITEM.LINE_HEIGHT : 0);
        });

        const barSize = CHART_DIMENSIONS.BAR.MIN_SIZE + 
                       (maxLines > 1 ? (maxLines - 1) * CHART_DIMENSIONS.BAR.SIZE_INCREMENT : 0);
        
        return {
            height: totalHeight + CHART_DIMENSIONS.PADDING.CHART_BOTTOM,
            barSize
        };
    };

    const renderYAxisTick = ({ x, y, payload }) => {
        const lines = truncateLabel(payload.value);
        const isArray = Array.isArray(lines);
        
        if (!isArray) {
            return (
                <text
                    x={x}
                    y={y}
                    dy={4}
                    textAnchor="end"
                    fill={theme.palette.text.primary}
                    style={{
                        ...TEXT_STYLES.AXIS_LABEL,
                        fontFamily: theme.typography.fontFamily,
                    }}
                >
                    {lines}
                </text>
            );
        }

        const lineCount = lines.length;
        const spacing = TEXT_STYLES.MULTILINE_LABEL.lineSpacing;
        const totalHeight = (lineCount - 1) * spacing;
        const startY = y - (totalHeight / 2);

        return (
            <g>
                {lines.map((line, index) => (
                    <text
                        key={index}
                        x={x}
                        y={startY + (index * spacing)}
                        textAnchor="end"
                        fill={theme.palette.text.primary}
                        style={{
                            ...TEXT_STYLES.MULTILINE_LABEL,
                            fontFamily: theme.typography.fontFamily,
                        }}
                    >
                        {line}
                    </text>
                ))}
            </g>
        );
    };

    const renderChart = () => {
        if (!selectedLegends.length || sortByLegendIndex === null) {
            return null;
        }

        // Create a mapping of original indices to filtered indices
        const selectedIndicesMap = selectedLegends.reduce((acc, originalIndex, filteredIndex) => {
            acc[originalIndex] = filteredIndex;
            return acc;
        }, {});

        // Filter data based on selected legends but keep original structure
        const filteredData = data.map(item => {
            const filteredAts = item.ats
                .map((a, index) => ({
                    ...a,
                    originalIndex: index,
                    show: selectedLegends.includes(index)
                }))
                .filter(a => a.show);

            return {
                ...item,
                ats: filteredAts
            };
        });

        // Sort data based on selected legend item
        const sortedData = [...filteredData].sort((a, b) => {
            const targetIndex = selectedIndicesMap[sortByLegendIndex];
            const valueA = a.ats[targetIndex]?.val ?? 0;
            const valueB = b.ats[targetIndex]?.val ?? 0;
            return sortOrder === 'desc' ? valueB - valueA : valueA - valueB;
        });

        // Define common props before using them
        const dimensions = calculateDimensions(sortedData);
        const customTooltipComponent = (props) => <CustomTooltip {...props} theme={theme} />;
        
        // Preserve original legend order
        const filteredLegend = legend
            .map((item, index) => ({
                ...item,
                originalIndex: index,
                show: selectedLegends.includes(index)
            }))
            .filter(item => item.show);

        const commonProps = {
            data: sortedData,
            legend: filteredLegend,
            dimensions,
            yAxisWidth,
            theme,
            renderYAxisTick,
            CustomTooltip: customTooltipComponent
        };

        // Apply transition props to commonProps
        const chartProps = {
            ...commonProps,
            className: isTransitioning ? 'transitioning' : '',
            style: {
                opacity: isTransitioning ? 0 : 1,
                transition: 'opacity 0.3s ease-in-out'
            }
        };

        // Render appropriate chart type
        switch (chartType) {
            case CHART_TYPES.TABLE:
                return (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell 
                                        sx={{ 
                                            fontWeight: 'bold',
                                            backgroundColor: theme.palette.grey[100],
                                            fontSize: '1rem'
                                        }}
                                    >
                                        Klausimas
                                    </TableCell>
                                    {filteredLegend.map((item) => (
                                        <TableCell 
                                            key={item.name}
                                            align="center"
                                            sx={{ 
                                                fontWeight: 'bold',
                                                backgroundColor: theme.palette.grey[100],
                                                fontSize: '1rem'
                                            }}
                                        >
                                            {item.name}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedData.map((row, index) => (
                                    <TableRow 
                                        key={index}
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: theme.palette.grey[50],
                                            }
                                        }}
                                    >
                                        <TableCell 
                                            component="th" 
                                            scope="row"
                                            sx={{ fontSize: '1rem' }}
                                        >
                                            {row.text}
                                        </TableCell>
                                        {row.ats.map((answer, i) => (
                                            <TableCell 
                                                key={i} 
                                                align="center"
                                                sx={{
                                                    color: theme.palette.text.secondary,
                                                    fontWeight: answer.val > 50 ? 'bold' : 'normal',
                                                    fontSize: '1rem'
                                                }}
                                            >
                                                {answer.val.toFixed(1)}%
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                );
            case CHART_TYPES.PIE:
                return <PieChartComponent {...commonProps} />;
            case CHART_TYPES.RADAR:
                return <RadarChartComponent {...commonProps} />;
            default:
                return <BarChartComponent {...commonProps} />;
        }
    };

    // Removed handleLegendsChange since we're always showing all answers

    return (
        <Box ref={chartRef} data-chart-type="matrix" sx={{ width: '100%', p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                {/* Controls container */}
                <Box data-export-hide="true" sx={{ 
                    display: 'flex',
                    alignItems: 'center', 
                    flexWrap: 'wrap',
                    justifyContent: 'start',
                    gap: 2,
                    width: '100%'
                }}>
                    {/* Removed Atsakymai filter - now showing all answers */}

                    {selectedLegends.length > 0 && (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Rikiuoti pagal</InputLabel>
                            <Select
                                value={sortByLegendIndex ?? ''}
                                onChange={(e) => setSortByLegendIndex(e.target.value)}
                                input={<OutlinedInput label="Rikiuoti pagal" />}
                            >
                                {selectedLegends.map((index) => (
                                    <MenuItem key={index} value={index}>
                                        {legend[index].name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {selectedLegends.length > 0 && (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Rikiavimo tvarka</InputLabel>
                            <Select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                input={<OutlinedInput label="Rikiavimo tvarka" />}
                            >
                                <MenuItem value="asc">Didėjimo tvarka</MenuItem>
                                <MenuItem value="desc">Mažėjimo tvarka</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                    
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Grafiko tipas</InputLabel>
                        <Select
                            value={chartType}
                            label="Grafiko tipas"
                            onChange={(e) => setChartType(e.target.value)}
                        >
                            <MenuItem value={CHART_TYPES.BAR}>Stulpelinė diagrama</MenuItem>
                            <MenuItem value={CHART_TYPES.PIE}>Skritulinė diagrama</MenuItem>
                            <MenuItem value={CHART_TYPES.RADAR}>Radar diagrama</MenuItem>
                            <MenuItem value={CHART_TYPES.TABLE}>Lentelė</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Chart container - only this will be exported */}
                <Paper
                    elevation={0}
                    sx={{
                        ...PAPER_STYLES.CONTAINER,
                        '@media print, [data-export="true"]': {
                            '& .recharts-text': {
                                fontFamily: 'Roboto, sans-serif !important',
                                fontSize: '20px !important'
                            },
                            '& .recharts-legend-item': {
                                fontFamily: 'Roboto, sans-serif !important',
                                fontSize: '20px !important'
                            },
                            '& .recharts-legend-wrapper': {
                                right: '0 !important',
                                left: 'auto !important',
                                bottom: 'auto !important'
                            }
                        }
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            maxWidth: '70%',
                            mb: 2,
                            ...TITLE_STYLES
                        }}
                    >
                        {chartTitle}
                    </Typography>
                    {renderChart()}
                </Paper>
            </Box>
        </Box>
    );
};
